import { SettingsPermissions } from "../../permissions";
import type {
  SettingAccess,
  SettingAgentEditability,
  SettingGroupDefinition,
  SettingOwner,
  SettingPermission,
  SettingRisk,
} from "../../settings-catalog/types";

export type SupportFlatTeamSettingGroupId =
  | "support.general"
  | "support.ticket_routing"
  | "support.ai"
  | "support.ai_channels"
  | "support.billing"
  | "support.voice"
  | "support.contact_form"
  | "support.csat"
  | "support.social"
  | "support.sms"
  | "support.faq"
  | "support.live_chat_widget"
  | "support.concierge_handoff";

const supportSettingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.support",
  product: "support",
};

const customerWidgetSettingsOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.customerWidget",
  product: "support",
};

const lowRisk: SettingRisk = { level: "low" };
const mediumCustomerFacingRisk: SettingRisk = {
  level: "medium",
  reason: "Changes can affect customer-visible support experiences.",
};

const requiresApproval: SettingAgentEditability = { mode: "requiresApproval" };

function directMerchantAccess(permission: SettingPermission): SettingAccess {
  return {
    read: { minVisibility: "merchant" },
    write: {
      mode: "grants",
      grants: [{ minVisibility: "merchant", method: "direct", permission }],
    },
  };
}

function readonlyInternalAccess(reason: string): SettingAccess {
  return {
    read: { minVisibility: "internal" },
    write: { mode: "none", reason },
  };
}

export const SupportFlatTeamSettingGroups = [
  {
    id: "support.general",
    title: "Support Inbox",
    description:
      "Core support enablement, sender identity, tags, reply defaults, and inbox display behavior.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: lowRisk,
    agentEditability: requiresApproval,
    synonyms: ["support settings", "inbox settings", "email support"],
    changeGuidance: {
      summary:
        "Confirm before changing global support enablement, sender identity, or reply defaults.",
    },
  },
  {
    id: "support.ticket_routing",
    title: "Ticket Routing",
    description:
      "Assignment, reassignment, merge, split, reopen, and archive behavior for support tickets.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    synonyms: ["assignment", "routing", "ticket ownership"],
  },
  {
    id: "support.ai",
    title: "Support AI",
    description:
      "Copilot, autopilot, live-chat automation, AI copy, guardrails, and AI tool windows.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_AI_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect automated customer-facing support responses and notifications.",
    },
    agentEditability: requiresApproval,
    synonyms: ["autopilot", "copilot", "support automation"],
    changeGuidance: {
      summary:
        "Explain which channels or ticket types AI will affect before enabling automation.",
      askBeforeChanging: [
        "Which support channels should AI handle?",
        "Should AI draft responses only, or resolve eligible tickets automatically?",
      ],
    },
  },
  {
    id: "support.ai_channels",
    title: "AI Channels",
    description:
      "Channel-specific enablement for support AI across email, social, SMS, and messaging providers.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_AI_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can allow AI to respond on additional customer-facing channels.",
    },
    agentEditability: requiresApproval,
    synonyms: ["AI platforms", "AI enabled platforms"],
  },
  {
    id: "support.billing",
    title: "Support Billing",
    description:
      "Support, AI, SMS, MMS, and voice billing configuration and usage counters.",
    productArea: "support",
    access: readonlyInternalAccess(
      "Support billing settings are owned by billing and subscription workflows.",
    ),
    owner: {
      kind: "teamSettings",
      path: "settings.support.billing",
      product: "billing",
    },
    affectsBilling: true,
    risk: {
      level: "high",
      reason:
        "Billing state and usage counters affect subscriptions and invoices.",
    },
    agentEditability: {
      mode: "never",
      reason:
        "Billing state and usage counters must be changed through billing workflows.",
    },
    synonyms: ["support pricing", "support usage", "support subscription"],
  },
  {
    id: "support.voice",
    title: "Voice Support",
    description:
      "Voice provider setup, call routing, availability, hold behavior, greetings, and voicemail.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect customer phone routing and connected voice provider behavior.",
    },
    agentEditability: requiresApproval,
    synonyms: ["phone support", "voice settings", "calls"],
  },
  {
    id: "support.contact_form",
    title: "Contact Form",
    description:
      "Hosted contact form copy, fields, actions, styling, reply identity, and upload behavior.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    synonyms: ["contact us form", "support form"],
  },
  {
    id: "support.csat",
    title: "CSAT Surveys",
    description:
      "Customer satisfaction survey enablement, copy, timing, methods, remarks, and header image.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect customer-facing survey sends and survey copy.",
    },
    agentEditability: requiresApproval,
    synonyms: ["satisfaction survey", "customer satisfaction"],
  },
  {
    id: "support.social",
    title: "Social Channels",
    description:
      "Facebook and Instagram comment handling, mention behavior, and Meta page data.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect public social-channel handling and connected Meta provider behavior.",
    },
    agentEditability: requiresApproval,
    synonyms: ["facebook support", "instagram support", "social comments"],
  },
  {
    id: "support.sms",
    title: "SMS Support",
    description: "SMS support enablement and compliance links.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: {
      level: "medium",
      reason:
        "Changes can affect customer-facing SMS support and compliance links.",
    },
    agentEditability: requiresApproval,
    synonyms: ["text support", "text messaging"],
  },
  {
    id: "support.faq",
    title: "FAQ Styling",
    description:
      "FAQ page branding, colors, imagery, collection display, and brand kit overrides.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: supportSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    synonyms: ["help center", "FAQ page", "support knowledge base"],
  },
  {
    id: "support.live_chat_widget",
    title: "Live Chat Widget",
    description:
      "Customer widget support chat copy, availability, fallback conversion, and visible support controls.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: customerWidgetSettingsOwner,
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    synonyms: ["chat widget", "support widget", "live chat"],
    dependencies: [
      {
        key: "support/ai/automateLiveChat",
        relationship: "related",
        description:
          "AI live-chat automation changes how widget conversations are handled.",
      },
      {
        group: "support.concierge_handoff",
        relationship: "related",
        description:
          "Concierge handoff settings affect widget escalation behavior.",
      },
    ],
  },
  {
    id: "support.concierge_handoff",
    title: "Concierge Handoff",
    description:
      "Concierge-to-support handoff mode, copy, transcript forwarding, ticket creation, and email collection.",
    productArea: "support",
    access: directMerchantAccess(SettingsPermissions.SET_SUPPORT_SETTINGS),
    owner: {
      kind: "teamSettings",
      path: "settings.concierge.ticketHandoffSettings",
      product: "support",
    },
    risk: mediumCustomerFacingRisk,
    agentEditability: requiresApproval,
    synonyms: ["AI handoff", "human handoff", "ticket handoff"],
    dependencies: [
      {
        group: "support.live_chat_widget",
        relationship: "related",
        description:
          "Handoff copy and routing often appears through the customer widget.",
      },
    ],
  },
] as const satisfies readonly SettingGroupDefinition<SupportFlatTeamSettingGroupId>[];

export const SupportFlatTeamSettingGroupById: Record<
  SupportFlatTeamSettingGroupId,
  SettingGroupDefinition<SupportFlatTeamSettingGroupId>
> = {
  "support.general": SupportFlatTeamSettingGroups[0],
  "support.ticket_routing": SupportFlatTeamSettingGroups[1],
  "support.ai": SupportFlatTeamSettingGroups[2],
  "support.ai_channels": SupportFlatTeamSettingGroups[3],
  "support.billing": SupportFlatTeamSettingGroups[4],
  "support.voice": SupportFlatTeamSettingGroups[5],
  "support.contact_form": SupportFlatTeamSettingGroups[6],
  "support.csat": SupportFlatTeamSettingGroups[7],
  "support.social": SupportFlatTeamSettingGroups[8],
  "support.sms": SupportFlatTeamSettingGroups[9],
  "support.faq": SupportFlatTeamSettingGroups[10],
  "support.live_chat_widget": SupportFlatTeamSettingGroups[11],
  "support.concierge_handoff": SupportFlatTeamSettingGroups[12],
};
